import { Layout } from '../components/Layout';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, CalendarDays, IndianRupee, Cake, Clock, Wallet, Upload, Link as LinkIcon, X, Pencil, Trash2, SlidersHorizontal, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

const STATUSES = ['enquiry', 'confirmed', 'in_production', 'ready', 'delivered', 'cancelled'];
const STATUS_LABEL = { enquiry: 'Enquiry', confirmed: 'Confirmed', in_production: 'In Production', ready: 'Ready', delivered: 'Delivered', cancelled: 'Cancelled' };
const STATUS_COLOR = {
  enquiry: 'bg-stone-100 text-stone-700',
  confirmed: 'bg-blue-100 text-blue-700',
  in_production: 'bg-amber-100 text-amber-800',
  ready: 'bg-emerald-100 text-emerald-700',
  delivered: 'bg-green-600 text-white',
  cancelled: 'bg-red-100 text-red-700',
};

const emptyForm = {
  customer_name: '', customer_phone: '', occasion: '', delivery_date: '', delivery_time: '',
  fulfillment: 'pickup', delivery_address: '', flavour: '', tier_flavours: [], weight: '', layers: '', shape: '',
  colour_theme: '', writing_on_cake: '', topper: '', flowers: '', accessories: '', packaging: '',
  egg_preference: 'eggless', cake_type: '', finish: '', order_source: 'walkin', reference_links: '', reference_images: [], internal_notes: '', customer_notes: '', total_amount: '',
};

const DRAFT_KEY = 'roch_order_draft';

const SERVINGS = {
  '500g': '4–6 servings', '1kg': '8–10 servings', '1.5kg': '12–15 servings', '2kg': '18–20 servings',
  '3kg': '28–30 servings', '4kg': '38–40 servings', '5kg': '48–50 servings',
};

const OPTION_FIELDS = {
  occasion: 'Occasion', finish: 'Cake Finish', flavour: 'Flavour', weight: 'Weight',
  tiers: 'Tiers', shape: 'Shape', colour_theme: 'Colour Theme', topper: 'Topper', flowers: 'Flowers / Accessories',
};

const CUSTOM = 'Custom';
// spec fields that support a free-text "Custom" entry
const SPEC_KEYS = ['occasion', 'finish', 'flavour', 'weight', 'layers', 'shape', 'colour_theme', 'topper', 'flowers'];
const withCustom = (arr) => {
  const list = (arr || []).filter((o) => o && String(o).trim() && String(o).toLowerCase() !== 'custom');
  return [...list, CUSTOM];
};

const OptionSelect = ({ value, onChange, options, testid }) => {
  const safe = (options || []).filter((o) => o && String(o).trim());
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>
        {safe.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        {safe.length === 0 && <div className="px-2 py-1.5 text-xs text-stone-400">No options — add via Manage Dropdowns</div>}
      </SelectContent>
    </Select>
  );
};

const FlavourSelect = ({ value, onChange, options, testid }) => {
  const groups = {};
  (options || []).forEach((o) => { if (!o || !o.value || !String(o.value).trim()) return; const g = o.group || 'Other'; (groups[g] = groups[g] || []).push(o.value); });
  const order = ['Classic', 'Premium', 'Other'];
  const keys = Object.keys(groups).sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select flavour" /></SelectTrigger>
      <SelectContent>
        {keys.map((g) => (
          <SelectGroup key={g}>
            <SelectLabel>{g === 'Other' ? 'Other' : `${g} Collection`}</SelectLabel>
            {groups[g].filter((v) => String(v).toLowerCase() !== 'custom').map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectGroup>
        ))}
        <SelectItem value={CUSTOM}>Custom (type your own)</SelectItem>
      </SelectContent>
    </Select>
  );
};

const MultiFlavourSelect = ({ value, onChange, options, testid }) => {
  const vals = Array.isArray(value) ? value : [];
  const list = [...new Set((options || []).map((o) => o && o.value).filter((v) => v && String(v).trim() && String(v).toLowerCase() !== 'custom'))];
  const toggle = (v) => onChange(vals.includes(v) ? vals.filter((x) => x !== v) : [...vals, v]);
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {list.map((v) => (
        <button
          type="button"
          key={v}
          data-testid={`${testid}-opt-${v}`}
          onClick={() => toggle(v)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${vals.includes(v) ? 'bg-primary text-white border-primary' : 'bg-white text-stone-600 border-stone-300 hover:border-primary'}`}
        >
          {v}
        </button>
      ))}
      {list.length === 0 && <span className="text-xs text-stone-400">No flavours — add via Manage Dropdowns</span>}
    </div>
  );
};

const OptionManager = ({ open, onOpenChange, options, reload }) => {
  const [field, setField] = useState('flavour');
  const [newVal, setNewVal] = useState('');
  const [newGroup, setNewGroup] = useState('Classic');
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const list = options.filter((o) => o.field === field);

  const add = async () => {
    if (!newVal.trim()) return;
    const body = { field, value: newVal.trim() };
    if (field === 'flavour') body.group = newGroup;
    try { await axios.post(`${API}/order-options`, body, auth()); setNewVal(''); toast.success('Option added'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };
  const saveEdit = async (id) => {
    try { await axios.put(`${API}/order-options/${id}`, { value: editVal.trim() }, auth()); setEditId(null); toast.success('Updated'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this option?')) return;
    try { await axios.delete(`${API}/order-options/${id}`, auth()); toast.success('Deleted'); reload(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="option-manager">
        <DialogHeader><DialogTitle>Manage Dropdown Options</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1">Field</label>
            <Select value={field} onValueChange={(v) => { setField(v); setEditId(null); }}>
              <SelectTrigger data-testid="opt-field-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(OPTION_FIELDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            {field === 'flavour' && (
              <Select value={newGroup} onValueChange={setNewGroup}>
                <SelectTrigger data-testid="opt-flavour-group" className="w-32 shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Classic">Classic</SelectItem><SelectItem value="Premium">Premium</SelectItem></SelectContent>
              </Select>
            )}
            <Input data-testid="opt-new-value" value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder={`New ${OPTION_FIELDS[field]} option`} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
            <Button data-testid="opt-add-btn" onClick={add} className="bg-primary text-white shrink-0"><Plus size={16} className="mr-1" /> Add</Button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {list.map((o) => (
              <div key={o.option_id} data-testid={`opt-row-${o.option_id}`} className="flex items-center gap-2 border border-stone-200 rounded-sm px-3 py-2">
                {editId === o.option_id ? (
                  <>
                    <Input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-8" />
                    <Button size="sm" onClick={() => saveEdit(o.option_id)} className="bg-primary text-white">Save</Button>
                    <button onClick={() => setEditId(null)} className="text-stone-400 text-sm px-1">Cancel</button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-stone-800">{o.value}{o.group && <span className="ml-2 text-xs text-stone-400">({o.group})</span>}</span>
                    <button data-testid={`opt-edit-${o.option_id}`} onClick={() => { setEditId(o.option_id); setEditVal(o.value); }} className="text-stone-400 hover:text-primary"><Pencil size={15} /></button>
                    <button data-testid={`opt-del-${o.option_id}`} onClick={() => del(o.option_id)} className="text-stone-400 hover:text-functional-error"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
            {list.length === 0 && <p className="text-center text-stone-400 py-6 text-sm">No options yet</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const tokenVal = () => localStorage.getItem('token');
const fileSrc = (p) => `${API}/files/${p}?auth=${tokenVal()}`;

const RefImageManager = ({ images = [], onChange }) => {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API}/uploads/image`, fd, { headers: { Authorization: `Bearer ${tokenVal()}` } });
        added.push(r.data.path);
      }
      onChange([...(images || []), ...added]);
      toast.success('Photo added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const extract = async () => {
    if (!link.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/uploads/extract`, { url: link.trim() }, auth());
      onChange([...(images || []), r.data.path]);
      setLink('');
      toast.success('Image added from link');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not get image from that link'); }
    finally { setBusy(false); }
  };

  const remove = (p) => onChange((images || []).filter((x) => x !== p));

  return (
    <div className="space-y-2" data-testid="ref-image-manager">
      {(images || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(images || []).map((p) => (
            <div key={p} className="relative group">
              <a href={fileSrc(p)} target="_blank" rel="noreferrer">
                <img src={fileSrc(p)} alt="reference" className="h-20 w-20 object-cover rounded-sm border border-stone-200" />
              </a>
              <button type="button" onClick={() => remove(p)} data-testid="remove-ref-image"
                className="absolute -top-2 -right-2 bg-functional-error text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label data-testid="upload-ref-photo" className="cursor-pointer inline-flex items-center gap-1 text-sm border border-stone-300 rounded-sm px-3 py-1.5 hover:bg-stone-50">
          <Upload size={14} /> Upload Photo
          <input type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
        </label>
        <div className="flex items-center gap-1 flex-1 min-w-[220px]">
          <Input data-testid="ref-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste Instagram / website link" className="h-9"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); extract(); } }} />
          <Button type="button" size="sm" variant="outline" disabled={busy || !link.trim()} onClick={extract} data-testid="extract-ref-link">
            <LinkIcon size={14} className="mr-1" /> Get
          </Button>
        </div>
      </div>
      {busy && <p className="text-xs text-stone-400">Working…</p>}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-stone-500 mb-1">{label}</label>
    {children}
  </div>
);

// Spec dropdown that reveals a free-text box when "Custom" is chosen.
const SpecField = ({ label, field, form, setForm, options, testid, note }) => {
  const isCustom = form[field] === CUSTOM;
  return (
    <Field label={label}>
      <OptionSelect testid={testid} value={form[field]} onChange={(v) => setForm({ ...form, [field]: v })} options={withCustom(options)} />
      {note}
      {isCustom && (
        <Input
          data-testid={`${testid}-custom`}
          className="mt-1 h-9"
          placeholder={`Type custom ${label.toLowerCase()}`}
          value={form[`${field}_custom`] || ''}
          onChange={(e) => setForm({ ...form, [`${field}_custom`]: e.target.value })}
        />
      )}
    </Field>
  );
};

export default function Orders({ user, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [draftRestored, setDraftRestored] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [options, setOptions] = useState([]);
  const [optOpen, setOptOpen] = useState(false);
  const [payAmt, setPayAmt] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [payType, setPayType] = useState('advance');

  const optionsFor = (field) => options.filter((o) => o.field === field).map((o) => o.value);
  const tierCount = Math.min(3, parseInt(String(form.layers || '').match(/(\d+)/)?.[1] || '1', 10) || 1);

  // Auto-save the New Order form as a local draft so nothing is lost on reload/session loss.
  const isEmptyForm = (f) => JSON.stringify(f) === JSON.stringify(emptyForm);
  useEffect(() => {
    if (!open || editingId) return;
    try {
      if (!isEmptyForm(form)) localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch (e) { /* noop */ }
  }, [form, open, editingId]);

  const openNewOrder = () => {
    setEditingId(null);
    let draft = null;
    try { const s = localStorage.getItem(DRAFT_KEY); draft = s ? JSON.parse(s) : null; } catch (e) { draft = null; }
    if (draft && !isEmptyForm({ ...emptyForm, ...draft })) {
      setForm({ ...emptyForm, ...draft });
      setDraftRestored(true);
    } else {
      setForm(emptyForm);
      setDraftRestored(false);
    }
    setOpen(true);
  };

  const openEditOrder = (o) => {
    setEditingId(o.order_id);
    setDraftRestored(false);
    setForm({
      ...emptyForm,
      ...o,
      total_amount: o.total_amount != null ? String(o.total_amount) : '',
      reference_links: Array.isArray(o.reference_links) ? o.reference_links.join('\n') : (o.reference_links || ''),
      reference_images: Array.isArray(o.reference_images) ? o.reference_images : [],
      tier_flavours: Array.isArray(o.tier_flavours) ? o.tier_flavours.map((s) => String(s).split(',').map((x) => x.trim()).filter(Boolean)) : [],
    });
    setDetail(null);
    setOpen(true);
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setForm(emptyForm);
    setDraftRestored(false);
    toast.success('Draft discarded');
  };
  const loadOptions = () => {
    axios.get(`${API}/order-options`, auth()).then((r) => setOptions(r.data.options)).catch(() => {});
  };

  const load = () => {
    axios.get(`${API}/orders`, auth()).then((r) => setOrders(r.data)).catch(() => {});
    axios.get(`${API}/orders-board/summary`, auth()).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadOptions(); }, []);

  const visible = useMemo(
    () => (filter === 'all' ? orders : orders.filter((o) => o.status === filter)),
    [orders, filter]
  );

  const createOrder = async () => {
    if (!form.customer_name || !form.customer_phone) return toast.error('Customer name & phone required');
    for (const k of SPEC_KEYS) {
      if (form[k] === CUSTOM && !(form[`${k}_custom`] || '').trim()) {
        return toast.error(`Please type the custom ${OPTION_FIELDS[k === 'layers' ? 'tiers' : k]?.toLowerCase() || k} value`);
      }
    }
    const resolved = { ...form };
    SPEC_KEYS.forEach((k) => {
      if (resolved[k] === CUSTOM) {
        resolved[k] = (form[`${k}_custom`] || '').trim() || 'Custom';
      }
      delete resolved[`${k}_custom`];
    });
    const payload = {
      ...resolved,
      total_amount: parseFloat(resolved.total_amount || 0),
      servings: SERVINGS[form.weight] || resolved.servings || null,
      tier_flavours: tierCount >= 2 ? (form.tier_flavours || []).slice(0, tierCount).map((t) => (Array.isArray(t) ? t.filter(Boolean).join(', ') : (t || '').trim())) : [],
      reference_links: resolved.reference_links ? resolved.reference_links.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (editingId) {
        await axios.put(`${API}/orders/${editingId}`, payload, auth());
        toast.success('Order updated');
        setOpen(false); setForm(emptyForm); setEditingId(null); load();
      } else {
        await axios.post(`${API}/orders`, payload, auth());
        toast.success('Order created');
        localStorage.removeItem(DRAFT_KEY);
        setDraftRestored(false);
        setOpen(false); setForm(emptyForm); load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const refreshDetail = async (id) => {
    const r = await axios.get(`${API}/orders/${id}`, auth());
    setDetail(r.data); load();
  };

  const changeStatus = async (status) => {
    await axios.put(`${API}/orders/${detail.order_id}`, { status }, auth());
    toast.success(`Marked ${STATUS_LABEL[status]}`);
    refreshDetail(detail.order_id);
  };

  const addPayment = async () => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) return toast.error('Enter amount');
    await axios.post(`${API}/orders/${detail.order_id}/payment`, { mode: payMode, amount: amt, type: payType }, auth());
    toast.success('Payment recorded');
    setPayAmt('');
    refreshDetail(detail.order_id);
  };

  const deleteOrder = async () => {
    if (!window.confirm(`Delete order ${detail.order_no}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/orders/${detail.order_id}`, auth());
      toast.success('Order deleted');
      setDetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const printKitchen = () => {
    const d = detail;
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const row = (label, val) => (val && String(val).trim()) ? `<tr><td class="k">${esc(label)}</td><td class="v">${esc(val)}</td></tr>` : '';
    const specRows = [
      row('Flavour', `${d.flavour || '—'}${d.egg_preference ? ` (${d.egg_preference})` : ''}`),
      (d.tier_flavours && d.tier_flavours.filter(Boolean).length) ? row('Tier Flavours', d.tier_flavours.map((f, i) => `Tier ${i + 1}: ${f || '—'}`).join('  |  ')) : '',
      row('Cake Finish', d.finish),
      row('Weight', `${d.weight || '—'}${d.servings ? ` · ${d.servings}` : ''}`),
      row('Tiers', d.layers),
      row('Shape', d.shape),
      row('Colour Theme', d.colour_theme),
      row('Writing on Cake', d.writing_on_cake),
      row('Topper', d.topper),
      row('Flowers / Accessories', d.flowers),
      row('Packaging', d.packaging),
    ].join('');
    const imgs = (d.reference_images || []).map((p) => `<img src="${fileSrc(p)}" />`).join('');
    const links = (d.reference_links || []).map((l) => `<div class="link">${esc(l)}</div>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Kitchen — ${esc(d.order_no)}</title>
    <style>
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; color: #111; margin: 0; }
      .hdr { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #111; padding-bottom: 8px; margin-bottom: 12px; }
      .hdr h1 { font-size: 22px; margin: 0; }
      .hdr .sub { font-size: 12px; color: #555; }
      .ono { text-align: right; }
      .ono .no { font-size: 20px; font-weight: 700; font-family: monospace; }
      .badge { display: inline-block; border: 2px solid #111; border-radius: 4px; padding: 2px 8px; font-size: 11px; font-weight: 700; text-transform: uppercase; margin-top: 4px; }
      .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; font-size: 13px; margin-bottom: 14px; }
      .meta b { display: inline-block; min-width: 95px; color: #555; font-weight: 600; }
      h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #999; padding-bottom: 3px; margin: 16px 0 8px; }
      table.spec { width: 100%; border-collapse: collapse; font-size: 14px; }
      table.spec td { padding: 6px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }
      table.spec td.k { width: 35%; color: #555; font-weight: 600; }
      table.spec td.v { font-weight: 600; }
      .notes { font-size: 13px; border: 1px solid #ccc; border-radius: 4px; padding: 10px; min-height: 48px; white-space: pre-wrap; }
      .imgs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
      .imgs img { height: 150px; width: 150px; object-fit: cover; border: 1px solid #999; border-radius: 4px; }
      .link { font-size: 11px; color: #333; word-break: break-all; }
      .foot { margin-top: 24px; display: flex; justify-content: space-between; font-size: 12px; color: #555; }
      .sigbox { border-top: 1px solid #111; width: 160px; text-align: center; padding-top: 4px; }
    </style></head><body>
      <div class="hdr">
        <div>
          <h1>ROCH Cake Studio</h1>
          <div class="sub">KITCHEN PRODUCTION FORM</div>
        </div>
        <div class="ono">
          <div class="no">${esc(d.order_no)}</div>
          <div class="badge">${esc((d.status || '').replace(/_/g, ' '))}</div>
        </div>
      </div>
      <div class="meta">
        <div><b>Customer</b> ${esc(d.customer_name || '—')}</div>
        <div><b>Phone</b> ${esc(d.customer_phone || '—')}</div>
        <div><b>Occasion</b> ${esc(d.occasion || '—')}</div>
        <div><b>Order Type</b> ${esc(d.order_source === 'online' ? 'Online' : 'Walk-in')}</div>
        <div><b>Delivery</b> ${esc(d.delivery_date || '—')} ${esc(d.delivery_time || '')}</div>
        <div><b>Fulfillment</b> ${esc(d.fulfillment || '—')}</div>
        <div><b>Quantity</b> 1 cake</div>
        <div><b>Total</b> ${fmt(d.total_amount)} (Bal ${fmt(d.balance)})</div>
        ${d.delivery_address ? `<div style="grid-column:1/3"><b>Address</b> ${esc(d.delivery_address)}</div>` : ''}
      </div>
      <h2>Cake Specifications</h2>
      <table class="spec"><tbody>${specRows}</tbody></table>
      <h2>Special Instructions / Notes</h2>
      <div class="notes">${esc([d.internal_notes, d.customer_notes].filter(Boolean).join('\n') || 'None')}</div>
      ${(imgs || links) ? `<h2>Reference</h2>${links}<div class="imgs">${imgs}</div>` : ''}
      <div class="foot">
        <div class="sigbox">Prepared by</div>
        <div class="sigbox">QC / Checked by</div>
      </div>
    </body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
    iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    const triggerPrint = () => {
      iframe.contentWindow.focus(); iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
    const images = Array.from(doc.images || []);
    const pending = images.filter((im) => !im.complete);
    if (pending.length) {
      let done = 0;
      const check = () => { done += 1; if (done >= pending.length) triggerPrint(); };
      pending.forEach((im) => { im.onload = check; im.onerror = check; });
      setTimeout(triggerPrint, 2500);
    } else {
      setTimeout(triggerPrint, 250);
    }
  };

  const sendOrderWhatsApp = () => {
    const d = detail;
    const num = (d.customer_phone || '').replace(/\D/g, '');
    const phone = num.length === 10 ? '91' + num : num;
    const lines = [
      `Hi ${d.customer_name || 'there'}! 🎂`,
      `Your cake order *${d.order_no}* with ROCH Cake Studio is *${STATUS_LABEL[d.status]}*.`,
      '',
      d.occasion ? `Occasion: ${d.occasion}` : '',
      d.flavour ? `Flavour: ${d.flavour}${d.weight ? ` (${d.weight})` : ''}` : '',
      d.delivery_date ? `Delivery: ${d.delivery_date} ${d.delivery_time || ''} (${d.fulfillment})` : '',
      '',
      `Total: ${fmt(d.total_amount)} · Paid: ${fmt(d.amount_paid)}`,
      d.balance > 0.01 ? `*Balance Due: ${fmt(d.balance)}*` : 'Fully Paid ✅',
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join('\n'));
    window.open(phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Orders</h1>
            <p className="text-stone-600 mt-1">Custom cake order job cards</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="manage-dropdowns-button" onClick={() => setOptOpen(true)} variant="outline" className="border-stone-300">
              <SlidersHorizontal size={18} className="mr-2" /> Manage Dropdowns
            </Button>
            <Button data-testid="new-order-button" onClick={openNewOrder} className="bg-secondary hover:bg-secondary-hover text-black">
              <Plus size={20} className="mr-2" /> New Order
            </Button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <div className="text-sm text-stone-500">Total Orders</div>
              <div className="text-2xl font-bold">{summary.total_orders}</div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <div className="text-sm text-stone-500">In Production</div>
              <div className="text-2xl font-bold text-amber-600">{summary.by_status?.in_production || 0}</div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <div className="text-sm text-stone-500">Ready</div>
              <div className="text-2xl font-bold text-emerald-600">{summary.by_status?.ready || 0}</div>
            </div>
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <div className="text-sm text-stone-500">Outstanding</div>
              <div className="text-2xl font-bold text-functional-error" data-testid="orders-outstanding">{fmt(summary.outstanding)}</div>
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap mb-5">
          {['all', ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`order-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === s ? 'bg-primary text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
            >
              {s === 'all' ? 'All' : STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((o) => (
            <button
              key={o.order_id}
              data-testid={`order-card-${o.order_id}`}
              onClick={() => setDetail(o)}
              className="text-left bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-secondary transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="font-mono text-sm text-primary font-semibold">{o.order_no}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLOR[o.status]}`}>{STATUS_LABEL[o.status]}</span>
              </div>
              <div className="font-semibold text-stone-900">{o.customer_name}</div>
              <div className="text-xs text-stone-500 mb-3">{o.customer_phone}</div>
              <div className="space-y-1 text-sm text-stone-600">
                {o.occasion && <div className="flex items-center gap-2"><Cake size={14} /> {o.occasion}{o.flavour ? ` · ${o.flavour}` : ''}</div>}
                {o.delivery_date && <div className="flex items-center gap-2"><CalendarDays size={14} /> {o.delivery_date} {o.delivery_time || ''}</div>}
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-stone-100">
                <span className="text-sm font-semibold">{fmt(o.total_amount)}</span>
                {o.balance > 0.01
                  ? <span className="text-xs text-functional-error font-medium">Bal {fmt(o.balance)}</span>
                  : <span className="text-xs text-emerald-600 font-medium">Paid</span>}
              </div>
            </button>
          ))}
          {visible.length === 0 && <p className="text-stone-400 col-span-full py-12 text-center">No orders</p>}
        </div>
      </div>

      <OptionManager open={optOpen} onOpenChange={setOptOpen} options={options} reload={loadOptions} />

      {/* New Order dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{editingId ? `Edit Order${form.customer_name ? ` · ${form.customer_name}` : ''}` : 'New Custom Cake Order'}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {draftRestored && (
              <div data-testid="draft-restored-banner" className="flex items-center justify-between gap-3 bg-secondary/15 border border-secondary/40 rounded-lg px-3 py-2 text-sm">
                <span className="text-stone-700">Draft restored from your last unsaved order.</span>
                <button type="button" data-testid="discard-draft-button" onClick={discardDraft} className="text-functional-error font-medium hover:underline shrink-0">Discard draft</button>
              </div>
            )}
            <div className="flex gap-2" data-testid="order-source-toggle">
              {[['walkin', 'Walk-in Order'], ['online', 'Online Order']].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  data-testid={`order-source-${val}`}
                  onClick={() => setForm({ ...form, order_source: val })}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${form.order_source === val ? 'bg-primary text-white border-primary' : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div>
              <h3 className="font-semibold text-stone-800 mb-2 text-sm uppercase tracking-wide">Customer</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *"><Input data-testid="order-cust-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></Field>
                <Field label="Phone *"><Input data-testid="order-cust-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></Field>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-stone-800 mb-2 text-sm uppercase tracking-wide">Event & Delivery</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Occasion"><OptionSelect testid="order-occasion" value={form.occasion} onChange={(v) => setForm({ ...form, occasion: v })} options={optionsFor('occasion')} /></Field>
                <Field label="Delivery Date"><Input type="date" data-testid="order-date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></Field>
                <Field label="Delivery Time"><Input type="time" value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} /></Field>
                <Field label="Fulfillment">
                  <Select value={form.fulfillment} onValueChange={(v) => setForm({ ...form, fulfillment: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="pickup">Pickup</SelectItem><SelectItem value="delivery">Delivery</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Address (if delivery)"><Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} /></Field>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-stone-800 mb-2 text-sm uppercase tracking-wide">Cake Specs</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Flavour">
                  <FlavourSelect testid="order-flavour" value={form.flavour} onChange={(v) => setForm({ ...form, flavour: v })} options={options.filter((o) => o.field === 'flavour')} />
                  {form.flavour === CUSTOM && (
                    <Input data-testid="order-flavour-custom" className="mt-1 h-9" placeholder="Type custom flavour" value={form.flavour_custom || ''} onChange={(e) => setForm({ ...form, flavour_custom: e.target.value })} />
                  )}
                </Field>
                <SpecField label="Cake Finish" field="finish" form={form} setForm={setForm} options={optionsFor('finish')} testid="order-finish" />
                <SpecField label="Weight" field="weight" form={form} setForm={setForm} options={optionsFor('weight')} testid="order-weight"
                  note={SERVINGS[form.weight] && <p className="text-xs text-secondary-foreground/70 text-stone-500 mt-1">≈ {SERVINGS[form.weight]}</p>} />
                <SpecField label="Tiers" field="layers" form={form} setForm={setForm} options={optionsFor('tiers')} testid="order-tiers" />
                <SpecField label="Shape" field="shape" form={form} setForm={setForm} options={optionsFor('shape')} testid="order-shape" />
                <SpecField label="Colour Theme" field="colour_theme" form={form} setForm={setForm} options={optionsFor('colour_theme')} testid="order-colour" />
                <Field label="Egg Preference">
                  <Select value={form.egg_preference} onValueChange={(v) => setForm({ ...form, egg_preference: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="eggless">Eggless</SelectItem><SelectItem value="egg">Egg</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Writing on Cake"><Input value={form.writing_on_cake} onChange={(e) => setForm({ ...form, writing_on_cake: e.target.value })} /></Field>
                <SpecField label="Topper" field="topper" form={form} setForm={setForm} options={optionsFor('topper')} testid="order-topper" />
                <SpecField label="Flowers / Accessories" field="flowers" form={form} setForm={setForm} options={optionsFor('flowers')} testid="order-flowers" />
              </div>
              {tierCount >= 2 && (
                <div className="mt-3 bg-secondary/5 border border-secondary/30 rounded-lg p-3" data-testid="tier-flavours-block">
                  <label className="block text-xs font-semibold text-stone-600 mb-2 uppercase tracking-wide">Flavour per Tier ({tierCount} tiers) — select one or more</label>
                  <div className="space-y-3">
                    {Array.from({ length: tierCount }).map((_, i) => (
                      <div key={i}>
                        <span className="block text-[11px] text-stone-400 mb-1">Tier {i + 1}{i === 0 ? ' (bottom)' : i === tierCount - 1 ? ' (top)' : ''}</span>
                        <MultiFlavourSelect
                          testid={`order-tier-flavour-${i + 1}`}
                          value={(form.tier_flavours || [])[i] || []}
                          onChange={(arr) => { const next = [...(form.tier_flavours || [])]; next[i] = arr; setForm({ ...form, tier_flavours: next }); }}
                          options={options.filter((o) => o.field === 'flavour')}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-stone-400 mt-2">Tap flavours to add/remove for each tier. The main Flavour above stays as the overall / base flavour.</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Reference Links (Pinterest/Instagram, comma or newline separated)">
                <textarea data-testid="order-refs" value={form.reference_links} onChange={(e) => setForm({ ...form, reference_links: e.target.value })} rows={2} className="w-full border border-stone-200 rounded-sm p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </Field>
              <div className="space-y-3">
                <Field label="Internal Notes"><Input value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} /></Field>
                <Field label="Total Amount (₹)"><Input data-testid="order-total" type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} /></Field>
              </div>
            </div>
            <Field label="Reference Photos (upload or paste a link to fetch the image)">
              <RefImageManager images={form.reference_images} onChange={(imgs) => setForm({ ...form, reference_images: imgs })} />
            </Field>
          </div>
          <Button data-testid="save-order-button" onClick={createOrder} className="w-full bg-primary text-white">{editingId ? 'Update Order' : 'Create Order'}</Button>
        </DialogContent>
      </Dialog>

      {/* Order detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="order-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>{detail.order_no} · {detail.customer_name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLOR[detail.status]}`}>{STATUS_LABEL[detail.status]}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <Button data-testid="edit-order-button" onClick={() => openEditOrder(detail)} variant="outline" className="border-stone-300 hover:bg-stone-100">
                    <Pencil size={16} className="mr-2" /> Edit Order
                  </Button>
                  <Button data-testid="print-kitchen-button" onClick={printKitchen} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    <Printer size={16} className="mr-2" /> Print Order Form for Kitchen
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div><span className="text-stone-500">Phone:</span> {detail.customer_phone}</div>
                  <div><span className="text-stone-500">Occasion:</span> {detail.occasion || '—'}</div>
                  <div><span className="text-stone-500">Delivery:</span> {detail.delivery_date || '—'} {detail.delivery_time || ''} ({detail.fulfillment})</div>
                  <div><span className="text-stone-500">Flavour:</span> {detail.flavour || '—'} {detail.egg_preference ? `(${detail.egg_preference})` : ''}</div>
                  {detail.tier_flavours?.filter(Boolean).length > 0 && (
                    <div className="col-span-2"><span className="text-stone-500">Tier Flavours:</span> {detail.tier_flavours.map((f, i) => `Tier ${i + 1}: ${f || '—'}`).join(' · ')}</div>
                  )}
                  <div><span className="text-stone-500">Finish:</span> {detail.finish || '—'}</div>
                  <div><span className="text-stone-500">Weight/Shape:</span> {detail.weight || '—'} {detail.shape || ''}{detail.servings ? ` · ${detail.servings}` : ''}</div>
                  <div><span className="text-stone-500">Source:</span> {detail.order_source === 'online' ? 'Online Order' : 'Walk-in'}</div>
                  <div><span className="text-stone-500">Theme:</span> {detail.colour_theme || '—'}</div>
                  <div className="col-span-2"><span className="text-stone-500">Writing:</span> {detail.writing_on_cake || '—'}</div>
                  {detail.delivery_address && <div className="col-span-2"><span className="text-stone-500">Address:</span> {detail.delivery_address}</div>}
                  {detail.internal_notes && <div className="col-span-2"><span className="text-stone-500">Notes:</span> {detail.internal_notes}</div>}
                </div>

                {detail.reference_links?.length > 0 && (
                  <div>
                    <div className="text-stone-500 mb-1">Reference Links</div>
                    <div className="flex flex-col gap-1">
                      {detail.reference_links.map((l, i) => <a key={i} href={l} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate">{l}</a>)}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-stone-500 mb-1">Reference Photos</div>
                  <RefImageManager
                    images={detail.reference_images || []}
                    onChange={async (imgs) => {
                      try {
                        await axios.put(`${API}/orders/${detail.order_id}`, { reference_images: imgs }, auth());
                        refreshDetail(detail.order_id);
                      } catch (e) { toast.error('Failed to update photos'); }
                    }}
                  />
                </div>

                {/* Status */}
                <div>
                  <div className="text-stone-500 mb-2 font-medium flex items-center gap-2"><Clock size={15} /> Update Status</div>
                  <div className="flex gap-2 flex-wrap">
                    {STATUSES.map((s) => (
                      <button key={s} data-testid={`status-btn-${s}`} onClick={() => changeStatus(s)} disabled={s === detail.status}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${s === detail.status ? 'bg-primary text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}>
                        {STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payments */}
                <div className="bg-stone-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium flex items-center gap-2"><Wallet size={15} /> Payments</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm">Total {fmt(detail.total_amount)} · Paid {fmt(detail.amount_paid)} · <span className={detail.balance > 0.01 ? 'text-functional-error font-semibold' : 'text-emerald-600 font-semibold'}>Bal {fmt(detail.balance)}</span></span>
                      <button data-testid="order-whatsapp-btn" onClick={sendOrderWhatsApp} title="Send update on WhatsApp" className="text-[#25D366] hover:opacity-80">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.715zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                      </button>
                    </div>
                  </div>
                  {detail.payments?.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs text-stone-600 border-b border-dashed border-stone-200 py-1">
                      <span className="capitalize">{p.type} · {p.mode}</span><span>{fmt(p.amount)}</span>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <Select value={payType} onValueChange={setPayType}><SelectTrigger className="w-28" data-testid="pay-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="advance">Advance</SelectItem><SelectItem value="balance">Balance</SelectItem></SelectContent></Select>
                    <Select value={payMode} onValueChange={setPayMode}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent></Select>
                    <Input data-testid="pay-amount" type="number" placeholder="Amount" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="flex-1" />
                    <Button data-testid="add-payment-button" onClick={addPayment} className="bg-secondary text-black"><IndianRupee size={16} /></Button>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <div className="text-stone-500 mb-2 font-medium">Timeline</div>
                  <div className="space-y-1">
                    {detail.timeline?.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-stone-600">
                        <span className="w-2 h-2 rounded-full bg-secondary" />
                        {STATUS_LABEL[t.status]} · {new Date(t.at).toLocaleString('en-IN')}
                      </div>
                    ))}
                  </div>
                </div>

                {user?.role === 'admin' && (
                  <div className="border-t border-stone-200 pt-4">
                    <Button data-testid="delete-order-button" onClick={deleteOrder} variant="outline" className="border-functional-error text-functional-error hover:bg-functional-error hover:text-white">
                      <Trash2 size={16} className="mr-2" /> Delete Order
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
